/**
 * Touch-first drive browser for phone viewports (mobile milestone 1).
 *
 * Data comes from the SAME `useFiles` hook DriveView uses (`@papan/files/ui`)
 * — TanStack-cached, realtime-subscribed — so lists stay live and the cache
 * contract in CLAUDE.md is untouched. Navigation is URL-driven: rows push
 * `${prefix}/folder/:id` and the route param feeds the hook, so deep links
 * and back/forward work like desktop.
 *
 * Files open through the existing SingleFileView route (`${prefix}/file/:id`)
 * — the standalone viewer already handles images/pdf/office/text.
 */
import { usePendingPointerGate, isPendingPointer } from '@papan/files/ui/pending-pointer-gate'
import { packageIsHallwayShell } from '@papan/files/ui/hallway-package'
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Search, MoreVertical } from 'lucide-react'
import { moveResultMessage } from '@papan/files/ui/protected-branches'
import { moveErrorText } from '@papan/files/ui/move-error-text'
import { extractPartialMobileText } from '@papan/files/ui/extract-partial-text'
import { useRealtime } from '@papan/realtime/client'
import { withMoveProgress } from '@papan/files/ui/move-progress'
import { useFiles, useDeleteRunner, SearchResults, DriveBreadcrumb, WhoHasAccessDialog, ShareInternallyDialog, PointerDownloadDialog, routePointerDownload, type FileItem } from '@papan/files/ui'
import { useBreadcrumbs } from '@papan/files/client/queries'
import { filesApi, type FileListItem, reportListedRowRefused } from '@papan/files/client'
import { ShareLinksDialog, useMiniAppShare } from '@papan/share-links/ui'
import { miniappAncestors } from '@papan/share-links/client'
import { ShareDialog } from '@papan/org-connections/ui'
import { FolderPickerDialog } from '@papan/workspace/ui'
import { useToast, useConfirm, useTheme, getThemeClass, useDraftState, useSwapScroll } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { isProjectFolder, presentsAsMiniapp, stripProjectFolderExt, stripMiniappFolderExt, isChainPointerRow, isSharedPointer } from '@papan/types'
import { requestMiniappView } from '@papan/collab/client'
import { useTenant } from '../hooks/useTenant'
import { recordFileOpen } from './recentOpens'
import { MobileFileSheet, MobileTypeIcon } from './MobileFileSheet'
import { canOfferAddToHomeScreen, startAddToHomeScreen, takeAddToHomeScreenHint } from '../lib/add-to-home-screen'

function formatSize(bytes: number): string {
  if (bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const diff = Date.now() - d.getTime()
  if (diff < 3600_000) return `${Math.max(1, Math.floor(diff / 60_000))}m ago`
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`
  if (diff < 7 * 86400_000) return `${Math.floor(diff / 86400_000)}d ago`
  return d.toLocaleDateString()
}

export function MobileDriveScreen() {
  const routerNavigate = useNavigate()
  const { t } = useTranslation('files')
  const toast = useToast()
  // #218 — arrived here through "Add to Home Screen": the page was just loaded
  // fresh at this folder, so Safari's manifest names it. Say what to do next.
  useEffect(() => {
    if (takeAddToHomeScreenHint()) toast.info(t('driveView.addToHomeScreen.hint', { defaultValue: 'Tap Share, then "Add to Home Screen".' }), 15000)
    // once per page load — the hint is consumed on read
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // No undo on mobile (it has no undo bar); the listing follows realtime.
  const { deleteItems } = useDeleteRunner()
  const { tenantId, isPersonal, activeOrgEmail } = useTenant()
  const { folderId } = useParams<{ folderId: string }>()
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`
  const recentScope = `${tenantId}:${activeOrgEmail ?? ''}`
  // Full path from the drive root to the current folder — same source as
  // desktop's DriveView header, so Library / Shared / My Space (and any
  // subfolder) get a trail back to the root "My Drive" listing.
  const { data: breadcrumbs = [] } = useBreadcrumbs(folderId ?? null)
  // #1264 — a folder whose row is gone (its delete may never have been announced). As the desktop
  // drive's dead-id recovery (DriveView): ONE breadcrumb read per navigation — an empty trail for an
  // explicit id, or a 403 / 404, means the row is gone. Say so, go back to the root, and have the list
  // that showed it re-read (the row drops). A network failure is not "gone": no bounce.
  useEffect(() => {
    if (!folderId) return
    let stale = false
    const recover = () => {
      if (stale) return
      reportListedRowRefused(folderId)
      toast.info(t('driveView.toast.folderGone'))
      routerNavigate(`${prefix}/folder`, { replace: true })
    }
    filesApi.getBreadcrumbs(folderId).then((crumbs) => { if (crumbs.length === 0) recover() }).catch((err: unknown) => {
      const status = (err as { status?: number } | null)?.status
      if (status === 403 || status === 404) recover()
    })
    return () => { stale = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId])

  const {
    files, loading, loadingMore, error, hasMore, total, loadMore, refresh, parentPermission,
  } = useFiles(folderId ?? null)
  // Re-list when access changes, as the desktop drive does (DriveView's
  // onOrgEvent / onPermissionChanged effect — same events, same 300ms debounce
  // so a change firing both refetches once). The rows' `effectivePermission`
  // and the folder's `parentPermission` decide what the ⋯ sheet offers (Share
  // internally, …); without this a person whose access was lowered kept the
  // old menu until they navigated. Not gated on isOrg, as on desktop: a share
  // link minted on a personal file fires permission:changed too.
  const { onOrgEvent, onPermissionChanged } = useRealtime()
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    const scheduleRefresh = () => { if (timer) clearTimeout(timer); timer = setTimeout(() => refresh(), 300) }
    const offOrg = onOrgEvent((event) => {
      if (event.type === 'org:access_changed' || event.type === 'org:role_changed') scheduleRefresh()
    })
    const offPerm = onPermissionChanged(() => scheduleRefresh())
    return () => { if (timer) clearTimeout(timer); offOrg(); offPerm() }
  }, [onOrgEvent, onPermissionChanged, refresh])
  // #897 — keeps its place across a phone/desktop layout swap (same key and
  // file-id rows as the desktop Drive list).
  const listRef = useRef<HTMLElement>(null)

  // `?reveal=<id>` (search, Storage): scroll the row into view and outline it — a chain is revealed,
  // never entered, from search (review round 2). ACCEPTED (Codex): a row past the loaded page (the
  // 101st+ child before "Load more") isn't scrolled to — the chain still never opens from search.
  const [searchParams] = useSearchParams()
  const revealId = searchParams.get('reveal')
  useEffect(() => {
    if (!revealId || !files.some((f) => f.id === revealId)) return
    document.querySelector(`[data-file-id="${CSS.escape(revealId)}"]`)?.scrollIntoView({ block: 'center' })
  }, [revealId, files])

  const { block: blockPendingPointer, dialogs: pendingPointerDialogs } = usePendingPointerGate({
    urlPrefix: prefix, rootLabel: t('driveView.rootFolder'),
    // The approve event doesn't carry the cleared flag — reload the listing (review B4).
    onApproved: () => refresh(),
  })

  // The ⋯ detail sheet target (null = closed).
  const [sheetFile, setSheetFile] = useState<FileItem | null>(null)

  // ⋯ sheet action flows (share / access dialogs, rename modal, delete confirm).
  const confirm = useConfirm()
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  // Shared with the desktop drive (#897, same key and shape): a phone/desktop
  // layout swap reopens the same file's share dialog here, with its unsaved work.
  const [shareLinksDialog, setShareLinksDialog, { clear: clearShareLinksDialog }] = useDraftState<{ file: FileItem } | null>(tenantId ? `drive:${tenantId}:shareLinks` : null, null)
  const shareLinkFile = shareLinksDialog?.file ?? null
  const setShareLinkFile = (f: FileItem) => setShareLinksDialog({ file: f })
  // "Share PDApp": the `.miniapp` folders on the current path (a listed file's
  // ancestors ARE the current folder's crumbs); the hook owns the picker + dialog.
  const mAnc = miniappAncestors(breadcrumbs)
  const miniAppShare = useMiniAppShare()
  const [shareOrgFile, setShareOrgFile] = useState<FileItem | null>(null)
  // Share internally — shared with the desktop drive (#897, same key and shape:
  // DriveView's `drive:<tenant>:shareInternally`), so a layout swap reopens the
  // dialog here and the dialog's own draft brings back the people being added.
  const [shareInternallyFile, setShareInternallyFile, { clear: clearShareInternally }] = useDraftState<FileItem | null>(tenantId ? `drive:${tenantId}:shareInternally` : null, null)
  const [accessFile, setAccessFile] = useState<FileItem | null>(null)
  const [renameTarget, setRenameTarget] = useState<FileItem | null>(null)
  const [renameValue, setRenameValue] = useState('')
  // Copy/Move/Extract destination picker (touch-native — one gesture, no Paste).
  const [picker, setPicker] = useState<{ mode: 'move' | 'copy' | 'extract'; target: FileItem } | null>(null)
  const commitPicker = async (destId: string) => {
    const p = picker
    setPicker(null)
    if (!p) return
    try {
      if (p.mode === 'move') {
        // #801: a split / replay says what moved and what stayed (mobile: the toast only).
        const r = await withMoveProgress(toast, t('driveView.toast.moving', { name: p.target.name }), (hooks) => filesApi.moveFile(p.target.id, { parentId: destId }, hooks))
        const msg = moveResultMessage(t, r)
        if (msg) toast.info(msg)
        else toast.success(t('driveView.toast.movedOne', { name: p.target.name }))
      }
      else if (p.mode === 'copy') {
        // #1397: the copy's per-item refusals (a damaged stored copy, a file left out of a folder) are the server's
        // words; "Copied" only when something landed — as the desktop drive reads them
        const r = await filesApi.copyFiles([p.target.id], destId)
        if (r.copied.length > 0 || (r.versioned?.length ?? 0) > 0) toast.success(t('driveView.toast.copiedOne', { name: p.target.name }))
        if (r.errors.length > 0) toast.error(r.errors.map((e) => `${p.target.name}: ${e.message}`).join('\n'))
      }
      else { const r = await filesApi.extractZip(p.target.id, destId); toast.success(t('driveView.toast.extracted', { count: r.filesExtracted, destination: p.target.name })) }
    } catch (e) {
      toast.error(p.mode === 'move' ? moveErrorText(e, t) : e instanceof Error ? e.message : t(p.mode === 'extract' ? 'driveView.toast.extractFailed' : 'driveView.toast.pasteFailed'))
      // #1459 Codex review — an extract that stopped part-way says what is already there
      const partly = p.mode === 'extract' ? extractPartialMobileText(e, t) : null
      if (partly) toast.warning(partly)
    }
  }
  // .zip → extract into the current folder; .miniapp → open its workspace (workshop).
  const extractHere = async (f: FileItem) => {
    try { const r = await filesApi.extractZip(f.id, f.parentId ?? folderId ?? null); toast.success(t('driveView.toast.extracted', { count: r.filesExtracted, destination: breadcrumbs[breadcrumbs.length - 1]?.name || t('driveView.rootFolder') })) }
    catch (e) {
      toast.error(e instanceof Error ? e.message : t('driveView.toast.extractFailed'))
      const partly = extractPartialMobileText(e, t)
      if (partly) toast.warning(partly)
    }
  }
  // Editing a mini-app lands on the Settings face by default (request it before
  // navigating so the editor comes up on Settings, not the running app).
  const editApp = (f: FileItem) => {
    if (presentsAsMiniapp(f.name, f.fileType, f.isFolder)) requestMiniappView(f.id, 'settings')
    routerNavigate(`${prefix}/workspace/${f.id}`)
  }

  const filePathOf = (f: FileItem) => `/papan/${breadcrumbs.map((b) => b.name).join('/')}${breadcrumbs.length ? '/' : ''}${f.name}`
  const [pointerDownload, setPointerDownload] = useState<FileItem | null>(null)
  const saveContents = async (f: FileItem) => {
    try {
      await filesApi.downloadItem({
        id: f.id, name: f.name, isFolder: f.isFolder,
        isPointer: f.fileType === 'shared' || f.fileType === 'chain',
      })
    }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Download failed') }
  }
  const saveMetadata = async (f: FileItem) => {
    try { await filesApi.downloadMetadata(f.id) }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Download failed') }
  }
  /**
   * A pointer row yields two different files, and this sheet used to take one
   * of them without asking — `downloadItem` always fetches CONTENTS. For a
   * DEAD pointer that is a near-empty zip instead of the JSON that repairs the
   * share, and the sheet had no other way to reach it: `downloadMetadata` left
   * the action registry when the drive collapsed its two menu items into one.
   *
   * `routePointerDownload` is the drive's own rule, so the two surfaces cannot
   * answer differently.
   */
  const downloadFile = async (f: FileItem) => {
    const route = routePointerDownload(f)
    if (route === 'metadata') return saveMetadata(f)
    if (route === 'choose') { setPointerDownload(f); return }
    return saveContents(f)
  }
  const startRename = (f: FileItem) => { setRenameValue(f.name); setRenameTarget(f) }
  const commitRename = async () => {
    const f = renameTarget
    const name = renameValue.trim()
    setRenameTarget(null)
    if (!f || !name || name === f.name) return
    try { await filesApi.moveFile(f.id, { name }) }
    catch (e) { toast.error(e instanceof Error ? e.message : t('driveView.toast.renameFailed')) }
  }
  const deleteFileFlow = async (f: FileItem) => {
    const ok = await confirm({
      title: t('driveView.confirm.deleteTitle'),
      message: t('driveView.confirm.deleteFile', { name: f.name }),
      confirmText: t('driveView.menu.delete'),
    })
    if (!ok) return
    // The drive's runner (#781): a big folder runs as a followed job, with the
    // same toast and plain-words refusals as desktop.
    await deleteItems([{ id: f.id, name: f.name }])
  }

  // In-folder search (mirrors desktop): the search button opens a scoped
  // search; results replace the list; the count-line chip widens the scope to
  // the whole drive. Auto-refresh made the old manual Refresh button redundant,
  // so this reclaims its slot.
  const [searchOpen, setSearchOpen] = useState(false)
  // Not while searching: the results aren't the folder's rows.
  useSwapScroll(searchOpen ? null : `drive:${folderId || 'root'}`, listRef, { items: '[data-file-id]', fraction: false })
  const [searchInput, setSearchInput] = useState('')
  const [searchScoped, setSearchScoped] = useState(true)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchInput.trim()), 250)
    return () => clearTimeout(id)
  }, [searchInput])
  // Leaving the folder exits search.
  useEffect(() => { setSearchOpen(false); setSearchInput('') }, [folderId])
  const closeSearch = () => { setSearchOpen(false); setSearchInput('') }

  const openSearchResult = async (f: FileListItem) => {
    closeSearch()
    // A package reached only as a HALLWAY browses as its folder (#1036), as on desktop.
    const startedAt = window.location.pathname // #1406: an answer that lands after the person moved on is dropped
    if ((isProjectFolder(f.name) || presentsAsMiniapp(f.name, f.fileType, f.isFolder)) && await packageIsHallwayShell(f)) {
      if (window.location.pathname !== startedAt) return
      return routerNavigate(`${prefix}/folder/${f.id}`, { state: { folderName: f.name } })
    }
    if (window.location.pathname !== startedAt) return
    if (isProjectFolder(f.name)) return routerNavigate(`${prefix}/workspace/${f.id}`)
    if (presentsAsMiniapp(f.name, f.fileType, f.isFolder)) return routerNavigate(`${prefix}/app/${f.id}`)
    // A chain opens from its own row (the D17 gate) — search rows don't say whether it needs approval.
    if (isChainPointerRow(f)) return routerNavigate(`${f.parentId && !f.parentId.startsWith('papan_') ? `${prefix}/folder/${f.parentId}` : `${prefix}/folder`}?reveal=${f.id}`)
    if (f.isFolder) return routerNavigate(`${prefix}/folder/${f.id}`, { state: { folderName: f.name } })
    routerNavigate(`${prefix}/file/${f.id}`)
  }
  const scopeFolderName = folderId ? (breadcrumbs[breadcrumbs.length - 1]?.name ?? t('driveView.rootFolder')) : t('driveView.rootFolder')

  const openRow = async (f: FileItem) => {
    if (f.shareBroken || f.chainBroken) {
      toast.info(f.shareNoAccess ? 'No access' : 'No longer available')
      return
    }
    // D17 / I114 — a chain that Needs approval doesn't open (Approve dialog or the reason).
    if (blockPendingPointer(f)) return
    // Packages launch (macOS-bundle model): a tap RUNS the package, not the
    // folder listing. `.project` opens its workspace; `.miniapp` runs the app
    // (MobileAppScreen, the phone AppWindow). Browsing the raw files is the
    // ⋯ sheet's "Browse as folder" (browseRow). A package reached only as a HALLWAY has no project
    // or app to open — it browses as its folder (#1036), as on desktop.
    const startedAt = window.location.pathname // #1406: an answer that lands after the person moved on is dropped
    if ((isProjectFolder(f.name) || presentsAsMiniapp(f.name, f.fileType, f.isFolder)) && await packageIsHallwayShell(f)) {
      if (window.location.pathname !== startedAt) return
      routerNavigate(`${prefix}/folder/${f.id}`, { state: { folderName: f.name } })
      return
    }
    if (window.location.pathname !== startedAt) return
    if (isProjectFolder(f.name)) { routerNavigate(`${prefix}/workspace/${f.id}`); return }
    // (#327) A `.shared` mount of someone else's `.miniapp` launches too.
    if (presentsAsMiniapp(f.name, f.fileType, f.isFolder)) { routerNavigate(`${prefix}/app/${f.id}`); return }
    // A `.chain` pointing at a folder browses its target in place.
    if (f.fileType === 'chain' && f.chainTargetId && f.chainTargetIsFolder) {
      routerNavigate(`${prefix}/folder/${f.chainTargetId}`, { state: { folderName: f.chainTargetName ?? f.name } })
      return
    }
    if (f.isFolder) {
      routerNavigate(`${prefix}/folder/${f.id}`, { state: { folderName: f.name } })
      return
    }
    // Track the open so it surfaces at the front of the Recent carousel. The
    // file's parent is the current folder — carry its name to disambiguate
    // same-named files in the carousel.
    const parentName = folderId ? (breadcrumbs[breadcrumbs.length - 1]?.name ?? null) : t('driveView.rootFolder')
    recordFileOpen(recentScope, f.id, f.name, parentName)
    routerNavigate(`${prefix}/file/${f.id}`)
  }

  // ⋯ sheet's "Browse files" — the package's underlying folder listing.
  const browseRow = (f: FileItem) => blockPendingPointer(f) || routerNavigate(`${prefix}/folder/${f.id}`, { state: { folderName: f.name } })

  // ⋯ sheet's "Open in new tab" — packages to their launcher (`.project` →
  // workspace, `.miniapp` → the app run surface), files to viewer.
  const openRowNewTab = (f: FileItem) => {
    if (blockPendingPointer(f)) return
    const isPackage = isProjectFolder(f.name) || presentsAsMiniapp(f.name, f.fileType, f.isFolder)
    const path = isProjectFolder(f.name) ? `${prefix}/workspace/${f.id}`
      : presentsAsMiniapp(f.name, f.fileType, f.isFolder) ? `${prefix}/app/${f.id}`
      : f.isFolder ? `${prefix}/folder/${f.id}`
      : `${prefix}/file/${f.id}`
    if (!isPackage) { window.open(path, '_blank'); return }
    // Opened on the tap (a popup blocker refuses a later open), then pointed at the package — or
    // at its folder when it is a hallway (#1036).
    const tab = window.open('', '_blank')
    void packageIsHallwayShell(f).then((shell) => {
      const url = shell ? `${prefix}/folder/${f.id}` : path
      if (tab) tab.location.href = url
      else window.open(url, '_blank')
    })
  }

  // Package rows show the stripped name (the extension is carried by the icon /
  // APP chip), matching desktop.
  const rowName = (f: FileItem): string =>
    presentsAsMiniapp(f.name, f.fileType, f.isFolder) ? stripMiniappFolderExt(f.name)
    : isProjectFolder(f.name) ? stripProjectFolderExt(f.name)
    : f.name

  const secondaryLine = (f: FileItem): string => {
    if (f.shareBroken) return f.shareNoAccess ? 'No access' : 'No longer available'
    if (f.chainBroken) return 'No longer available'
    if (f.fileType === 'shared' && f.sharedFromName) return `Shared by ${f.sharedFromName}`
    // A chain that Needs approval never shows where it points (only the Approve dialog does).
    if (isPendingPointer(f)) return t('driveView.chain.needsApproval')
    if (f.fileType === 'chain' && f.chainTargetName) return `→ ${f.chainTargetName}`
    // Folders show their modified date too (and size when the backend reports a
    // non-zero one); files show size · modified.
    return [formatSize(f.size), formatWhen(f.updatedAt)].filter(Boolean).join(' · ')
  }

  return (
    <div className="flex h-full flex-col">
      {/* Breadcrumb bar — branding lives in the fixed MobileHeader above.
          Section nav (Home · Library · My Space · Projects · Shared) hangs off
          the header cutout's dropdown (MobileDriveSectionMenu); the trail here
          walks back from the current folder to the root "My Drive" listing —
          same source as desktop's DriveView header. */}
      <div className="flex items-center gap-1 border-b themed-border px-2 py-1 shrink-0">
        {/* Shared with desktop — same space-driven squeeze (root → drive
            glyph, hallway → … menu, current folder last) + package badges. */}
        <DriveBreadcrumb
          breadcrumbs={breadcrumbs}
          rootLabel={t('driveView.rootFolder')}
          onNavigate={(id) => routerNavigate(id ? `${prefix}/folder/${id}` : `${prefix}/folder`)}
        />
        {!searchOpen && (
          <button
            onClick={() => setSearchOpen(true)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full themed-icon active:themed-bg-tertiary"
            aria-label={t('driveView.search.open')}
          >
            <Search size={16} />
          </button>
        )}
      </div>

      {/* Dedicated in-folder search row — the breadcrumb above stays put (the
          bar is space-tight); scoped to the current folder, widen via the
          results' scope chip. */}
      {searchOpen && (
        <div className="flex items-center gap-1 border-b themed-border px-2 py-1 shrink-0">
          <Search size={16} className="ml-1 shrink-0 themed-text-muted" />
          <input
            autoFocus
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={searchScoped
              ? t('driveView.search.placeholder', { name: scopeFolderName })
              : t('driveView.search.placeholderEverywhere')}
            className="min-w-0 flex-1 bg-transparent px-1 py-1.5 text-sm outline-none themed-text placeholder:themed-text-muted"
          />
          <button
            onClick={closeSearch}
            className="shrink-0 rounded px-3 py-1.5 text-sm themed-text-secondary active:themed-bg-tertiary"
          >
            {t('common:cancel')}
          </button>
        </div>
      )}

      <main ref={listRef} className="flex-1 overflow-y-auto overscroll-contain">
        {searchOpen ? (
          <div className="px-3 py-3">
            <SearchResults
              q={debouncedSearch}
              rootId={searchScoped ? (folderId ?? null) : null}
              scope={{ folderName: scopeFolderName, scoped: searchScoped, onToggle: () => setSearchScoped((s) => !s) }}
              onOpen={openSearchResult}
            />
          </div>
        ) : (
          <>
        {loading && files.length === 0 && (
          <p className="px-4 py-10 text-center text-sm themed-text-muted">Loading…</p>
        )}
        {error && (
          <p className="px-4 py-10 text-center text-sm text-red-400">{error}</p>
        )}
        {!loading && !error && files.length === 0 && (
          <p className="px-4 py-10 text-center text-sm themed-text-muted">This folder is empty</p>
        )}
        {files.map((f) => {
          const broken = !!(f.shareBroken || f.chainBroken)
          const sub = secondaryLine(f)
          const isMiniapp = presentsAsMiniapp(f.name, f.fileType, f.isFolder)
          return (
            // Row = an open area (tap) + a separate touch ⋯ target (can't nest
            // buttons), so tap-to-open and "more options" don't fight.
            <div
              key={f.id}
              data-file-id={f.id}
              className={`flex items-center ${broken ? 'opacity-45' : ''} ${revealId === f.id ? 'ring-1 ring-blue-400 rounded' : ''}`}
            >
              <button
                onClick={() => openRow(f)}
                className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 pr-1 text-left active:themed-bg-tertiary"
              >
                <MobileTypeIcon file={f} size={22} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-[15px] themed-text">{rowName(f)}</span>
                    {isMiniapp && (
                      <span className="text-[9px] font-semibold tracking-wide px-1 py-px rounded file-badge-app shrink-0">APP</span>
                    )}
                  </span>
                  {sub && (
                    <span className="block truncate text-xs themed-text-muted">{sub}</span>
                  )}
                </span>
              </button>
              <button
                onClick={() => setSheetFile(f)}
                aria-label="More options"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full themed-icon active:themed-bg-tertiary"
              >
                <MoreVertical size={18} />
              </button>
            </div>
          )
        })}
        {hasMore && !loading && (
          <button
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="w-full px-4 py-3 text-center text-sm text-blue-400 active:themed-bg-tertiary disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : `Load more (${files.length} of ${total})`}
          </button>
        )}
          </>
        )}
      </main>

      <MobileFileSheet
        file={sheetFile}
        isOrg={!isPersonal}
        parentPermission={parentPermission}
        // The row as re-listed after an access change: an open sheet's menu follows it.
        currentRow={sheetFile ? files.find(r => r.id === sheetFile.id) ?? null : null}
        onClose={() => setSheetFile(null)}
        onBrowse={browseRow}
        onEditApp={editApp}
        onOpenAsApp={(f) => routerNavigate(`${prefix}/app/${f.id}`)}
        onExtractHere={extractHere}
        onExtractTo={(f) => setPicker({ mode: 'extract', target: f })}
        onOpenNewTab={openRowNewTab}
        onShareLink={setShareLinkFile}
        onShareMiniApp={() => miniAppShare.openMiniAppShare(mAnc)}
        miniappAncestors={mAnc}
        onShareOrg={setShareOrgFile}
        onShareInternally={setShareInternallyFile}
        onWhoHasAccess={setAccessFile}
        onRename={startRename}
        onCopyTo={(f) => setPicker({ mode: 'copy', target: f })}
        onMoveTo={(f) => setPicker({ mode: 'move', target: f })}
        onDownload={downloadFile}
        onDelete={deleteFileFlow}
        onAddToHomeScreen={canOfferAddToHomeScreen() ? (f) => startAddToHomeScreen(`${prefix}/folder/${f.id}`) : undefined}
      />

      {pendingPointerDialogs}

      {/* "Share PDApp" picker + folder-targeted share dialog (owned by the hook). */}
      {miniAppShare.node}

      {shareLinkFile && (
        <ShareLinksDialog
          fileId={shareLinkFile.id}
          fileName={shareLinkFile.name}
          filePath={filePathOf(shareLinkFile)}
          isFolder={shareLinkFile.isFolder}
          isAppEligible={presentsAsMiniapp(shareLinkFile.name, shareLinkFile.fileType, shareLinkFile.isFolder)}
          onClose={clearShareLinksDialog}
        />
      )}
      {shareOrgFile && (
        <ShareDialog
          folderId={shareOrgFile.id}
          folderName={shareOrgFile.name}
          isFolder={shareOrgFile.isFolder}
          folderPath={filePathOf(shareOrgFile)}
          myPermission={shareOrgFile.effectivePermission}
          onClose={() => setShareOrgFile(null)}
        />
      )}
      {shareInternallyFile && (
        <ShareInternallyDialog
          fileId={shareInternallyFile.id}
          fileName={shareInternallyFile.name}
          tenantId={tenantId}
          isOrg={!isPersonal}
          isFolder={shareInternallyFile.isFolder}
          myPermission={shareInternallyFile.effectivePermission}
          // Refresh, as the desktop drive does: a grant changes the row's access.
          onClose={() => { clearShareInternally(); refresh() }}
        />
      )}
      {picker && (
        <FolderPickerDialog
          tenantId={tenantId}
          excludeIds={[picker.target.id]}
          title={picker.mode === 'extract'
            ? t('driveView.menu.extractTo', { defaultValue: 'Extract to…' })
            : (picker.mode === 'move' ? t('driveView.menu.move', { defaultValue: 'Move' }) : t('driveView.menu.copyTo', { defaultValue: 'Copy to…' })) + ` "${picker.target.name}"`}
          confirmLabel={picker.mode === 'extract'
            ? t('driveView.picker.extractHere', { defaultValue: 'Extract here' })
            : picker.mode === 'move' ? t('driveView.picker.moveHere', { defaultValue: 'Move here' }) : t('driveView.picker.copyHere', { defaultValue: 'Copy here' })}
          onSelect={commitPicker}
          onClose={() => setPicker(null)}
        />
      )}
      {pointerDownload && (
        <PointerDownloadDialog
          files={[pointerDownload]}
          themeClass={themeClass}
          t={t}
          onCancel={() => setPointerDownload(null)}
          onConfirm={async (choice) => {
            const row = pointerDownload
            setPointerDownload(null)
            if (choice === 'pointer') return saveMetadata(row)
            return saveContents(row)
          }}
        />
      )}

      {accessFile && (
        <WhoHasAccessDialog
          folderId={accessFile.id}
          folderName={accessFile.name}
          folderPath={filePathOf(accessFile)}
          tenantId={tenantId}
          isFolder={accessFile.isFolder}
          myPermission={accessFile.effectivePermission}
          isReceivedShare={isSharedPointer(accessFile.name) || !!accessFile.sharedFromName}
          // Personal drives have no roles/teams — hide that tab (Links /
          // Outsiders / Apps still apply). #437 follow-up: the sheet now offers
          // Who-has-access on personal drives, so the dialog must handle it.
          hidePeopleTeams={isPersonal}
          onClose={() => setAccessFile(null)}
        />
      )}
      {renameTarget && createPortal(
        <div className={`${themeClass} ws-clear fixed inset-0 z-[210] flex items-center justify-center p-6`}>
          <div className="absolute inset-0 bg-black/50" onClick={() => setRenameTarget(null)} />
          <div className="relative w-full max-w-sm rounded-2xl themed-bg-secondary border themed-border p-4 shadow-2xl">
            <div className="mb-2 text-sm font-medium themed-text">{t('driveView.menu.rename')}</div>
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void commitRename(); if (e.key === 'Escape') setRenameTarget(null) }}
              className="w-full themed-input rounded-lg px-3 py-2 text-sm"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setRenameTarget(null)} className="rounded-md px-3 py-1.5 text-sm themed-text-secondary active:themed-bg-tertiary">{t('common:cancel')}</button>
              <button onClick={() => void commitRename()} className="rounded-md px-3 py-1.5 text-sm themed-btn-primary">{t('common:save')}</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
